import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../auth/AuthContext.jsx";
import AuthShell from "../components/AuthShell.jsx";

export default function Login() {
  const { t } = useTranslation();
  const { login, isAuthenticated, bootstrapping } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/dashboard";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  if (!bootstrapping && isAuthenticated) return <Navigate to={from} replace />;

  async function onSubmit(event) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      if (err?.response?.status === 429) {
        setError(t("auth.tooManyAttempts"));
      } else {
        setError(err?.response?.data?.detail || t("auth.invalidCredentials"));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell
      title={t("auth.loginTitle")}
      subtitle={t("auth.loginSubtitle")}
      footer={
        <span>
          {t("auth.newHere")}{" "}
          <Link to="/register" className="text-brand-700 hover:underline">
            {t("auth.createAccount")}
          </Link>
        </span>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700">
            {t("common.email")}
          </label>
          <input
            type="email"
            className="input mt-1"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            autoFocus
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700">
            {t("common.password")}
          </label>
          <input
            type="password"
            className="input mt-1"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
          />
        </div>
        {error && (
          <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
            {error}
          </div>
        )}
        <button type="submit" className="btn-primary w-full" disabled={busy}>
          {busy ? t("auth.loginBusy") : t("auth.loginButton")}
        </button>
        <div className="text-center text-sm">
          <Link to="/reset-password" className="text-slate-500 hover:underline">
            {t("auth.forgotPassword")}
          </Link>
        </div>
      </form>
    </AuthShell>
  );
}
